import { useState } from 'react';

import { SmartApplyApiError } from '../lib/api-client.js';
import { useAuth } from '../lib/auth-context.js';
import { useRoute } from '../lib/route.js';
import { AuthLayout } from '../components/auth/AuthLayout.js';
import { PasswordInput } from '../components/auth/PasswordInput.js';
import { OAuthButtons } from '../components/auth/OAuthButtons.js';

interface PendingTotp {
  challengeToken: string;
  expiresAt: string;
}

export function LoginRoute() {
  const { login, verifyTotp } = useAuth();
  const { navigate } = useRoute();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [pendingTotp, setPendingTotp] = useState<PendingTotp | null>(null);

  async function handleLoginSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await login({ email, password });
      if (res.kind === 'authenticated') {
        navigate('/');
      } else {
        setPendingTotp({ challengeToken: res.challengeToken, expiresAt: res.expiresAt });
      }
    } catch (err) {
      setError(err instanceof SmartApplyApiError ? err.message : 'Falha ao entrar.');
    } finally {
      setLoading(false);
    }
  }

  if (pendingTotp) {
    return (
      <TotpStep
        challengeToken={pendingTotp.challengeToken}
        expiresAt={pendingTotp.expiresAt}
        onVerify={async (code) => {
          await verifyTotp({ challengeToken: pendingTotp.challengeToken, code });
          navigate('/');
        }}
        onCancel={() => {
          setPendingTotp(null);
          setPassword('');
        }}
      />
    );
  }

  return (
    <AuthLayout
      title="Entrar"
      subtitle="Acesse sua conta para continuar."
      badge="autenticação"
      side={
        <div className="sticky top-12 space-y-6 border-l border-[var(--border-subtle)] pl-6">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
              ainda não tem conta?
            </p>
            <a
              href="#/register"
              className="mt-2 inline-block font-serif text-base text-[var(--ink-primary)] underline-offset-4 hover:underline"
            >
              Criar conta →
            </a>
          </div>
          <p className="border-t border-[var(--border-subtle)] pt-4 font-sans text-xs italic leading-relaxed text-[var(--ink-tertiary)]">
            Senha em Argon2id, sessões revogáveis no banco. Sem mensagens
            esquisitas — se errou, dizemos só que errou.
          </p>
        </div>
      }
    >
      <form
        onSubmit={handleLoginSubmit}
        className="card-paper rounded-sm px-7 py-7"
        style={{ borderLeft: '3px solid var(--ink-primary)' }}
      >
        <div className="space-y-5">
          <div>
            <label
              htmlFor="email"
              className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]"
            >
              E-mail
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 w-full rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper)] px-3 py-2 font-mono text-sm text-[var(--ink-primary)] focus:border-[var(--accent-saffron)] focus:outline-none"
            />
          </div>

          <PasswordInput
            id="password"
            label="Senha"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          {error && (
            <div className="rounded-sm border border-[var(--accent-crimson-border)] bg-[var(--accent-crimson-bg)] px-4 py-2.5">
              <p className="font-sans text-sm text-[var(--accent-crimson)]">{error}</p>
            </div>
          )}

          <div className="flex items-center justify-between">
            <a
              href="#/forgot-password"
              className="font-sans text-xs text-[var(--ink-tertiary)] underline-offset-4 hover:underline"
            >
              Esqueci a senha
            </a>
            <button
              type="submit"
              disabled={loading || !email || !password}
              className="rounded-sm bg-[var(--ink-primary)] px-5 py-2 font-sans text-sm text-[var(--bg-paper)] transition-opacity hover:opacity-90 disabled:opacity-40"
            >
              {loading ? 'Entrando...' : 'Entrar'}
            </button>
          </div>
        </div>

        <div className="mt-6">
          <OAuthButtons />
        </div>
      </form>
    </AuthLayout>
  );
}

function TotpStep({
  challengeToken: _challengeToken,
  expiresAt,
  onVerify,
  onCancel,
}: {
  challengeToken: string;
  expiresAt: string;
  onVerify: (code: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await onVerify(code.trim());
    } catch (err) {
      setError(err instanceof SmartApplyApiError ? err.message : 'Falha ao verificar.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout
      title="Confirmação 2FA"
      subtitle="Digite o código do seu app autenticador (6 dígitos) ou um recovery code (xxxx-xxxx-xxxx)."
      badge="2 de 2"
      side={
        <div className="sticky top-12 space-y-4 border-l border-[var(--border-subtle)] pl-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
            sessão expira em
          </p>
          <p className="font-mono text-xs tabular-nums text-[var(--ink-secondary)]">
            {new Date(expiresAt).toLocaleTimeString('pt-BR')}
          </p>
          <p className="border-t border-[var(--border-subtle)] pt-4 font-sans text-xs italic leading-relaxed text-[var(--ink-tertiary)]">
            Perdeu o celular? Use um dos recovery codes que você salvou ao
            ativar o 2FA.
          </p>
        </div>
      }
    >
      <form
        onSubmit={handleSubmit}
        className="card-paper rounded-sm px-7 py-7"
        style={{ borderLeft: '3px solid var(--accent-saffron)' }}
      >
        <div className="space-y-5">
          <div>
            <label
              htmlFor="totp"
              className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]"
            >
              Código
            </label>
            <input
              id="totp"
              type="text"
              inputMode="text"
              autoFocus
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456 ou abcd-1234-ef56"
              className="mt-2 w-full rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper)] px-3 py-2 font-mono text-base tabular-nums text-[var(--ink-primary)] focus:border-[var(--accent-saffron)] focus:outline-none"
            />
          </div>

          {error && (
            <div className="rounded-sm border border-[var(--accent-crimson-border)] bg-[var(--accent-crimson-bg)] px-4 py-2.5">
              <p className="font-sans text-sm text-[var(--accent-crimson)]">{error}</p>
            </div>
          )}

          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={onCancel}
              className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)] hover:text-[var(--ink-primary)]"
            >
              voltar
            </button>
            <button
              type="submit"
              disabled={loading || !code}
              className="rounded-sm bg-[var(--ink-primary)] px-5 py-2 font-sans text-sm text-[var(--bg-paper)] transition-opacity hover:opacity-90 disabled:opacity-40"
            >
              {loading ? 'Verificando...' : 'Verificar'}
            </button>
          </div>
        </div>
      </form>
    </AuthLayout>
  );
}
